import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../context/ToastContext';
import PrimaryActionButton from '../common/PrimaryActionButton';
import WeeklyHoursEditor from '../common/WeeklyHoursEditor';
import StoreService from '../../services/storeService';
import {
  PERIOD_ERROR,
  getWeekErrorCode,
  periodsByDay,
  weekFromApi,
  weekToApi,
} from '../../utils/weeklyHoursUtils';

const DEFAULT_EXTRA_PERIOD = { from: '19:00', to: '21:00' };

const ERROR_MESSAGE_KEYS = {
  [PERIOD_ERROR.INVALID_PERIOD]: 'storeRegistration.validation.openTimeAfterCloseTime',
  [PERIOD_ERROR.OVERLAP_PERIODS]: 'stores.authorizationHours.overlapPeriods',
  [PERIOD_ERROR.OVERLAP_REFERENCE]: 'stores.authorizationHours.overlapOpening',
};

const nextExtraPeriod = (last) => ({ from: last?.to || DEFAULT_EXTRA_PERIOD.from, to: DEFAULT_EXTRA_PERIOD.to });

const StoreAuthorizationHoursPanel = ({ storeId, store, canUpdate, onSaved }) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [hours, setHours] = useState(() => weekFromApi(store?.authorizationHours, DEFAULT_EXTRA_PERIOD));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setHours(weekFromApi(store?.authorizationHours, DEFAULT_EXTRA_PERIOD));
  }, [store?.authorizationHours]);

  const openingByDay = useMemo(() => periodsByDay(store?.openingHours), [store?.openingHours]);

  const showValidationError = (errorCode) => showError(t(ERROR_MESSAGE_KEYS[errorCode]));

  const handleSave = async () => {
    const errorCode = getWeekErrorCode(hours, openingByDay);
    if (errorCode) {
      showValidationError(errorCode);
      return;
    }

    try {
      setSaving(true);
      const saved = await StoreService.updateAuthorizationHours(storeId, weekToApi(hours));
      showSuccess(t('stores.authorizationHours.saved'));
      onSaved?.(saved);
    } catch (error) {
      showError(error?.response?.data?.message || t('stores.error.updateAuthorizationHours'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3, md: 4 }, minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('stores.authorizationHours.helper')}
      </Typography>

      <WeeklyHoursEditor
        value={hours}
        onChange={setHours}
        canUpdate={canUpdate}
        referencePeriodsByDay={openingByDay}
        referenceLabel={t('stores.authorizationHours.openingHoursLabel')}
        blockReferenceOverlap
        nextPeriodFor={nextExtraPeriod}
        applyAllFieldLabel={t('stores.tabs.onRequestHours')}
        onValidationError={showValidationError}
      />

      {canUpdate && (
        <PrimaryActionButton onClick={handleSave} disabled={saving}>
          {saving ? t('common.saving') : t('stores.authorizationHours.save')}
        </PrimaryActionButton>
      )}
    </Box>
  );
};

export default StoreAuthorizationHoursPanel;
